import { useState, useRef, useCallback, useEffect } from "react";
import type { TFunction } from "i18next";
import { extractErrorMessage } from "@/shared/utils";
import { baseApi } from "@/shared/api";
import { useAppDispatch } from "@/shared/hooks";
import { runtimeConfig } from "@/shared/config/runtime";
import {
  FINALIZATION_TIMEOUT_MS,
  FINALIZING_POLL_INTERVAL,
  POST_SIGNING_INVALIDATION_DELAY,
  POST_SIGNING_TAGS,
  withPollJitter,
} from "../consts/postSigning";
import { useGetSigningDocumentV2Query } from "../api/mobileSigning";
import { signWithNCALayer, NCALayerError, NCALAYER_CANCELLED_CODE } from "../lib/ncalayer";
import { arrayBufferToBase64 } from "../utils";
import { getSigningPhaseKey, type SigningPhase } from "./signingPhase";

interface PromptState {
  title: string;
  text: string;
  variant: "success" | "error";
}

interface UseOrderSigningOptions {
  previewFn: () => Promise<Blob>;
  signFn: (cmsSignature: string, fileBase64?: string) => Promise<unknown>;
  onSuccess: () => void;
  setPrompt: (prompt: PromptState) => void;
  t: TFunction;
  skipFileUpload?: boolean;
  signingDocumentPublicId?: string | null;
  resolveErrorText?: (error: unknown) => string | null;
}

export function useOrderSigning(options: UseOrderSigningOptions) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const dispatch = useAppDispatch();

  const [phase, setPhase] = useState<SigningPhase>("idle");
  const phaseRef = useRef<SigningPhase>("idle");
  const isLoading = phase !== "idle";
  const loaderText = phase === "idle" ? "" : optionsRef.current.t(getSigningPhaseKey(phase, runtimeConfig.isDemo));

  const abortRef = useRef<AbortController | null>(null);
  // Only up to the signature. Once the CMS is in hand the submit must finish:
  // abandoning it there spends the user's signature and stores nothing.
  const canCancel = phase === "previewing" || phase === "signing";

  const [waitStartedAt, setWaitStartedAt] = useState<number | null>(null);
  const isFinalizing = phase === "finalizing";
  const { data: signingDocument } = useGetSigningDocumentV2Query(
    options.signingDocumentPublicId as string,
    {
      skip: !isFinalizing || !options.signingDocumentPublicId,
      pollingInterval: withPollJitter(FINALIZING_POLL_INTERVAL),
      skipPollingIfUnfocused: true,
    },
  );

  const finishSigning = useCallback(function finishSigning() {
    phaseRef.current = "idle";
    setPhase("idle");
    setWaitStartedAt(null);
    dispatch(baseApi.util.invalidateTags([...POST_SIGNING_TAGS]));
    optionsRef.current.onSuccess();
  }, [dispatch]);

  useEffect(
    function closeWhenSettled() {
      if (!isFinalizing) return;
      if (signingDocument?.settlement?.state === "pending") return;
      if (!signingDocument) return;
      finishSigning();
    },
    [isFinalizing, signingDocument, finishSigning],
  );

  useEffect(
    function capFinalizationWait() {
      if (!isFinalizing || waitStartedAt === null) return;
      const timer = setTimeout(finishSigning, FINALIZATION_TIMEOUT_MS);
      return function cleanup() {
        clearTimeout(timer);
      };
    },
    [isFinalizing, waitStartedAt, finishSigning],
  );

  const handleSign = useCallback(async function handleSign() {
    if (phaseRef.current !== "idle") return;
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      phaseRef.current = "previewing";
      setPhase("previewing");
      const blob = await optionsRef.current.previewFn();
      if (controller.signal.aborted) {
        throw new NCALayerError("ncalayer.errors.cancelled", NCALAYER_CANCELLED_CODE);
      }

      const arrayBuffer = await blob.arrayBuffer();
      const base64 = arrayBufferToBase64(arrayBuffer);

      phaseRef.current = "signing";
      setPhase("signing");
      const result = await signWithNCALayer(base64, controller.signal);

      phaseRef.current = "submitting";
      setPhase("submitting");
      if (optionsRef.current.skipFileUpload) {
        await optionsRef.current.signFn(result.signature);
      } else {
        await optionsRef.current.signFn(result.signature, base64);
      }

      if (optionsRef.current.signingDocumentPublicId) {
        phaseRef.current = "finalizing";
        setPhase("finalizing");
        setWaitStartedAt(Date.now());
        return;
      }

      phaseRef.current = "idle";
      setPhase("idle");
      setTimeout(() => {
        dispatch(baseApi.util.invalidateTags([...POST_SIGNING_TAGS]));
      }, POST_SIGNING_INVALIDATION_DELAY);
      optionsRef.current.onSuccess();
    } catch (err: unknown) {
      phaseRef.current = "idle";
      setPhase("idle");

      if (err instanceof NCALayerError) {
        if (err.code === NCALAYER_CANCELLED_CODE) {
          return;
        }
        optionsRef.current.setPrompt({
          title: optionsRef.current.t("signing.errors.signingFailedTitle"),
          text: optionsRef.current.t(err.message),
          variant: "error",
        });
        return;
      }

      optionsRef.current.setPrompt({
        title: optionsRef.current.t("signing.errors.signingFailedTitle"),
        text:
          optionsRef.current.resolveErrorText?.(err) ??
          extractErrorMessage(err, "signing.errors.signingFailedText", optionsRef.current.t),
        variant: "error",
      });
    }
  }, [dispatch]);

  const handleCancel = useCallback(function handleCancel() {
    abortRef.current?.abort();
  }, []);

  return { phase, isLoading, canCancel, handleSign, handleCancel, loaderText };
}
